import { app } from "@microsoft/teams-js";
import { Checkbox, FormControlLabel, FormHelperText, Grid, InputAdornment, TextField, Tooltip } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Config from "../../config";
import { ArrowForwordIcon, FalconLogo, FbIcon, HidePassword, InstaIcon, LinkdinIcon, LoginScreenMobileBG, MicrosoftIcon, ShowPassword, WebexLoginIcon, WebIcon, YoutubeIcon } from "../../icon";
import { AuthService } from "../../services/AuthService";
import { AppRoutes } from "../routes/route.path";
import { ViewTextLabels, appNote, buttonLabels } from "../shared/constants";
import { validateForm } from "../shared/form-validation";
import TeamsUserAlert from "../shared/teams.redirection.alert";
import MicrosoftSsoLogin from "./MicrosoftSSO/microSoft-team-login.component";
import { UserService } from "../../services/UserService";
import { useSelector } from "react-redux";
import * as selector from "../../selectors/selectors"
import Carousel from "./Carousel";
import LoginScreenRight1 from '../../icons/loginScreenRight1.svg';
import LoginScreenRight2 from '../../icons/loginScreenRight2.svg';

interface IState {
    email: string;
    password: string;
    keepLogin: boolean;
    showError: boolean;
    showPassword: boolean;
    errorDescription: React.ReactNode;
    errors: {
        email: string;
        password: string;
    };
    IsLoading: boolean;
    IsSSOLoading: boolean;
    WebexLoading: boolean;
    isDownloadIconHovered: {
        name: string;
        isHovered: boolean;
    };
    hostingEnvironment: string;
}

const Login = () => {
    const PageRef = useRef<HTMLDivElement>(null);
    const location = useLocation();
    const navigate = useNavigate();
    const passwordRef = useRef<HTMLInputElement | null>(null);

    const IsMobileDevice: boolean = useSelector(selector.isMobileDevice);
    const IsDarkMode: boolean = useSelector(selector.isDarkMode);
    const IsTeamsApp: boolean = useSelector(selector.isTeamsApp)

    const [state, setState] = useState<IState>({
        email: "",
        password: "",
        keepLogin: true,
        showError: false,
        showPassword: false,
        errorDescription: '',
        errors: {
            email: "",
            password: "",
        },
        IsLoading: false,
        IsSSOLoading: false,
        isDownloadIconHovered: {
            name: "",
            isHovered: false
        },
        hostingEnvironment: 'unknown',
        WebexLoading: false
    });

    const fromSubmit = (event) => {
        event.preventDefault();
        const password = passwordRef.current?.value || "";
        if (
            validateForm(
                { email: state.email, password: password },
                (errors) => {
                    setState((prevState) => ({ ...prevState, errors: { ...prevState.errors, ...errors }, showError: false }));
                }
            )
        ) {
            setState((prevState) => ({
                ...prevState,
                IsLoading: true,
                showError: false, // Reset showError when submitting
            }));

            AuthService.login(state.email, state.password, state.keepLogin, navigate)
                .then((res) => {
                    if (res.status === 200) {
                        setState((prevState) => ({ ...prevState, IsLoading: false }));
                    }
                })
                .catch((err) => {
                    setState((prevState) => ({ ...prevState, IsLoading: false }));

                    if (err && err.response && err.response.data) {
                        if (err.response.data.error_description === "INACTIVE_USER") {
                            setState((prevState) => ({
                                ...prevState,
                                errorDescription: (
                                    <span>
                                        Sorry, your account is locked. Please contact your organization admin or{" "}
                                        <a className="underline" href="https://share.hsforms.com/1uSaLr6GxTn-Mqid2VwLqzgcauh6" target="_blank">
                                            contact us
                                        </a>{" "}
                                        for further queries.
                                    </span>
                                ),
                            }));
                        } else if (err.response.data.error_description === "INACTIVE_PHONE_NUMBER") {
                            setState((prevState) => ({
                                ...prevState,
                                errorDescription: (
                                    <span>
                                        Sorry, your Phone Number is inactive. Please contact your organization admin or{" "}
                                        <a className="underline" href="https://share.hsforms.com/1uSaLr6GxTn-Mqid2VwLqzgcauh6" target="_blank">
                                            contact us
                                        </a>{" "}
                                        for further queries.
                                    </span>
                                ),
                            }));
                        } else if (err.response.data.error_description === "INACTIVE_ORGANIZATION") {
                            setState((prevState) => ({
                                ...prevState,
                                errorDescription: (
                                    <span>
                                        Sorry, your organization is disabled. Please{" "}
                                        <a className="underline" href="https://share.hsforms.com/1uSaLr6GxTn-Mqid2VwLqzgcauh6" target="_blank">
                                            contact us
                                        </a>{" "}
                                        for further queries.
                                    </span>
                                ),
                            }));
                        } else if (err.response.data.error_description === "INVALID_EMAIL") {
                            setState((prevState) => ({ ...prevState, errorDescription: appNote.INVALID_EMAIL }));
                        } else if (err.response.data.error_description === "INVALID_PASSWORD") {
                            setState((prevState) => ({ ...prevState, errorDescription: appNote.INVALID_PASWORD }));
                        } else if (err.response.data.error_description === "ONLY_MICROSOFT_SSO_LOGIN_ALLOWED_FOR_THE_ORGANIZATION") {
                            setState((prevState) => ({ ...prevState, errorDescription: appNote.ONLY_MICROSOFT_LOGIN_ALLOWED }));
                        } else if (err.response.data.error_description === "INACTIVE_SEAT_TYPE") {
                            setState((prevState) => ({ ...prevState, errorDescription: appNote.SEAT_SUBSCRIPTION_CANCELED }));
                        } else {
                            setState((prevState) => ({ ...prevState, errorDescription: appNote.OTHERS }));
                        }
                    }

                    setState((prevState) => ({ ...prevState, showError: true }));
                });
        }
    };


    const handleChange = (name: string, value: any) => {
        validateForm({ [name]: value },
            (errors) => {
                setState((prevState) => ({ ...prevState, errors: { ...state.errors, ...errors } }))
            })
        setState((prevState) => ({ ...prevState, [name]: value }));
    };
    const handleArrowNavigation = (e) => {
        const active = document.activeElement;

        if (active) {
            let index;

            switch (e.keyCode) {
                case 37:
                    index = active.getAttribute("data-left-arrow-index") || active.getAttribute("data-arrow-index");
                    break;
                case 38:
                    index = active.getAttribute("data-up-arrow-index") || active.getAttribute("data-arrow-index");
                    break;
                case 39:
                    index = active.getAttribute("data-right-arrow-index") || active.getAttribute("data-arrow-index");
                    break;
                case 40:
                    index = active.getAttribute("data-down-arrow-index") || active.getAttribute("data-arrow-index");
                    break;
                default:
                    break;
            }

            const targetElement = document.querySelector(`[data-arrow-index="${index}"]`) as HTMLElement;
            targetElement && targetElement.focus();
        }
    };

    const componentDidMount = () => {
        AuthService.ssoLoginConfig(location.search, navigate);
        if (!state.IsSSOLoading) {
            setState((prevState) => ({ ...prevState, IsSSOLoading: false }))
        }
        if (!state.WebexLoading) {
            setState((prevState) => ({ ...prevState, WebexLoading: false }))
        }

        const element = PageRef.current;
        if (element) {
            element.addEventListener('keydown', handleArrowNavigation);
        }

        if (IsTeamsApp) {
            app.initialize().then((context) => {
                app.notifySuccess();
            });
            app.getContext().then((context) => {
                if (context.app.host.clientType === "desktop") {
                    setState((prevState) => ({ ...prevState, hostingEnvironment: 'desktop' }));
                } else if (context.app.host.clientType === "web") {
                    setState((prevState) => ({ ...prevState, hostingEnvironment: 'browser' }));
                } else if (context.app.host.clientType === "android") {
                    setState((prevState) => ({ ...prevState, hostingEnvironment: 'android' }));
                } else if (context.app.host.clientType === "ios") {
                    setState((prevState) => ({ ...prevState, hostingEnvironment: 'ios' }));
                } else {
                    setState((prevState) => ({ ...prevState, hostingEnvironment: 'unknown' }));
                }
            })
        }
    }

    const componentWillUnmount = () => {
        const element = PageRef.current;
        if (element) {
            element.removeEventListener('keydown', handleArrowNavigation);
        }
    }

    const onClickShowPassword = () => {
        setState((prevState) => ({ ...prevState, showPassword: !state.showPassword }));
    }
    const ssoLogin = (provider: string) => {
        provider === "Webex" ? sessionStorage.setItem('isWebexLoginLoading', 'true') : sessionStorage.setItem('isSSOLoginLoading', 'true')
        setState((prevState) => (provider === "Webex" ? { ...prevState, WebexLoading: true } : { ...prevState, IsSSOLoading: true }))
        window.location.href = `${Config.AUTH_URL}/api/SSOAuth/ValidateUser?isMobile=false${provider ? `&provider=${provider}` : ''}`;
    }



    useEffect(() => {
        componentDidMount();

        return () => {
            componentWillUnmount();
        };
    }, [location]);

    useEffect(() => {
        if (!!AuthService.loggedIn() && !!AuthService.is2FACompleted() && !!AuthService.isNewUser()) {
            navigate(AppRoutes.RESET_PASSWORD_NEW);
        }
        else if (!!AuthService.loggedIn() && !AuthService.is2FACompleted()) {
            navigate(AppRoutes.TWO_FACTOR_AUTHENTICATION)
        }
        else if (!!AuthService.isUserLoggedIn()) {
            navigate(UserService.hasSysAdminRole() ? AppRoutes.PHONE_NUMBERS : IsMobileDevice ? AppRoutes.CONVERSATIONS : AppRoutes.DASHBOARD)
        }
        else if (location.pathname === AppRoutes.SIGN_IN && location.search.includes("Result")) {
            navigate(location.pathname + location.search)
        }
        else {
            navigate(AppRoutes.SIGN_IN);
            localStorage.clear();
        }
    }, []);

    return (
        <>
            <h1 className="hide-element">Falkon SMS</h1>
            <Helmet>
                <meta name="description" content="Welcome back to FalkonSMS. Sign in to start texting and connecting with your customers via your Toll-Free, Landline, VoIP and MS Teams direct routing numbers." />
            </Helmet>
            <div className={`m-auto bg-color-secondary ${IsTeamsApp ? "h-v-100 o-y-scroll" : "h-v-100"}`}>
                {IsMobileDevice && <div className="p-absolute t-0 r-0" style={{ zIndex: -1 }}>
                    <LoginScreenMobileBG />
                </div>
                }
                {IsMobileDevice &&
                    <div className='w-p-100 h-p-10 d-flex align-end pl-6'>
                        <FalconLogo className={"w-20"} />
                    </div>
                }
                <div className={`d-flex  ${IsMobileDevice ? "h-p-90" : "h-p-100"} w-p-100`} ref={PageRef}>
                    {!IsMobileDevice && <div className={`d-flex align-center flex-direction h-p-100 w-p-45 ${IsDarkMode ? "bg-color-sidebar-bg" : "signin-left-bg"} `}>
                        <Carousel ParentComponent={ViewTextLabels.SIGN_IN} />
                    </div>
                    }
                    <div className={` ${IsMobileDevice ? "w-p-100" : "w-p-55  justify-center"} h-p-100 d-flex flex-direction p-relative`}>
                        {!IsMobileDevice &&
                            <div className="p-absolute t-0 l-0 w-30" style={{ zIndex: -1 }}>
                                <img src={LoginScreenRight1} alt="" className="w-p-100" />
                            </div>
                        }
                        {!IsMobileDevice &&
                            <div className="p-absolute b-0 r-0 w-60" style={{ zIndex: -1 }}>
                                <img src={LoginScreenRight2} alt="" className="w-p-100" />
                            </div>
                        }
                        <div className={`h-p-85 d-flex justify-center flex-direction align-center w-p-100 `} >
                            <form className={`${IsMobileDevice ? "w-p-70" : "w-p-50 min-w-50 mt-auto pb-8"} d-flex flex-direction justify-center align-center`} onSubmit={fromSubmit}>
                                <div className="w-p-100 d-flex justify-center min-h-10 h-p-10">
                                    <span className={`h1-semibold ${IsDarkMode ? "" : "color-login-headers"} w-space`}>
                                        Sign in to Falkon SMS
                                    </span>
                                </div>
                                <TextField
                                    error={
                                        !!state.errors.email
                                    }
                                    variant="standard"
                                    label="Email Address*"
                                    fullWidth
                                    id="email"
                                    placeholder="yourname@domain.com"
                                    name="email"
                                    autoComplete="email"
                                    autoFocus
                                    value={state.email}
                                    onChange={(e) => handleChange(e.target.name, e.target.value)}
                                    className={`unauth-border ${state.errors.email ? "unauth-border-error" : ""} w-p-100 min-h-6 h-p-8`}
                                    inputProps={{ tabIndex: 1, "data-arrow-index": 6 }}
                                    InputProps={{
                                        classes: { input: 'h4-regular' }
                                    }}
                                />
                                <FormHelperText className={`w-p-100 h5-regular color-primary-danger t-align-left min-h-3 h-p-3`}>
                                    {state.errors.email}
                                </FormHelperText>
                                <TextField
                                    label="Enter Your Password*"
                                    error={
                                        !!state.errors.password
                                    }
                                    inputRef={passwordRef}
                                    variant="standard"
                                    placeholder="******"
                                    fullWidth
                                    name="password"
                                    type={state.showPassword ? "text" : "password"}
                                    id="password"
                                    autoComplete="current-password"
                                    // value={state.password}
                                    onChange={(e) => handleChange(e.target.name, e.target.value)}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position='start' className="c-pointer" onClick={onClickShowPassword}>
                                                {state.showPassword ? <HidePassword /> : <ShowPassword />}
                                            </InputAdornment>),
                                        classes: { input: 'h4-regular' }
                                    }}
                                    className={`unauth-border disableEdgeIcon ${state.errors.password ? "unauth-border-error" : ""} min-h-6 h-p-8`}
                                    inputProps={{ maxLength: 20, tabIndex: 2, "data-arrow-index": 7 }}
                                    title=""
                                />
                                <div className="w-p-100 d-flex justify-between min-h-4 h-p-3 align-center">
                                    <FormHelperText className={`h5-regular color-primary-danger t-align-left`}>
                                        {state.errors.password}
                                    </FormHelperText>
                                    {!IsMobileDevice && <Grid item className="t-align-right">
                                        <Link tabIndex={5}
                                            data-arrow-index={10}
                                            data-up-arrow-index={6}
                                            data-down-arrow-index={7}
                                            data-left-arrow-index={2}
                                            data-right-arrow-index={10}
                                            to={AppRoutes.FORGOT_PASSWORD} className={"color-login-link c-pointer text-style w-space h5-regular f-link-hover"}> {appNote.FORGOT_YOUR_PASSWORD} </Link>
                                    </Grid>}
                                </div>
                                {IsMobileDevice && <Grid item className="t-align-center p-2">
                                    <Link tabIndex={5}
                                        data-arrow-index={10}
                                        data-up-arrow-index={6}
                                        data-down-arrow-index={7}
                                        data-left-arrow-index={2}
                                        data-right-arrow-index={10}
                                        to={AppRoutes.FORGOT_PASSWORD} className={"color-login-link c-pointer text-style w-space h5-regular f-link-hover"}> {appNote.FORGOT_YOUR_PASSWORD} </Link>
                                </Grid>}
                                <div className={"w-p-100 min-h-3"}>
                                    <FormControlLabel classes={{ label: "w-space h5-regular color-keepme-sign-label" }} control={
                                        <Checkbox
                                            checked={state.keepLogin}
                                            value={state.keepLogin}
                                            onChange={(e: any) => setState((prevState) => ({ ...prevState, keepLogin: e.target.checked }))}
                                            inputProps={{ tabIndex: 3, "data-arrow-index": 8, "data-up-arrow-index": 7, "data-down-arrow-index": 9, "data-left-arrow-index": 2, "data-right-arrow-index": 8 } as any}
                                            classes={{ checked: "selected-checkbox" }} name="keepSignedIn" />}
                                        label="Keep me signed in" />
                                </div>
                                <button
                                    data-arrow-index={9}
                                    data-up-arrow-index={8}
                                    data-down-arrow-index={9}
                                    data-left-arrow-index={2}
                                    data-right-arrow-index={9}
                                    tabIndex={4} type="submit"
                                    className={`c-pointer justify-center d-flex w-space h4-regular w-fill br-2 t-align-center primary-btn w-p-100 align-center unauth-screen-button-gredient m-0 h-p-4 min-h-5`} style={{ textTransform: "none" }}>
                                    {state.IsLoading && <span className="mr-1 loader-primary my-auto"></span>}
                                    <span className={"my-auto mr-1"}>{buttonLabels.SIGN_IN}</span>
                                    <ArrowForwordIcon />
                                </button>
                                <div className={"t-align-center w-p-100 min-h-2"}>
                                    <span className={"color-primary-danger h5-regular"}>
                                        {state.errorDescription}
                                    </span>
                                </div>
                                <div className={`w-p-100 t-align-center h5-regular color-primary-accent ${IsMobileDevice && "p-1"}`} style={{ zIndex: 1 }}>
                                    <span >
                                        {appNote.NEW_TO_FALKON_SMS}&nbsp;
                                    </span>
                                    <span>
                                        {!IsTeamsApp ? <Link tabIndex={5}
                                            data-arrow-index={10}
                                            data-up-arrow-index={6}
                                            data-down-arrow-index={7}
                                            data-left-arrow-index={2}
                                            data-right-arrow-index={10}
                                            to={AppRoutes.SIGN_UP} className={"color-login-link c-pointer text-style w-space m-auto"}> {"Create an Account"}</Link> :
                                            <TeamsUserAlert
                                                Link="https://meetings.hubspot.com/falkon-systems/meeting"
                                                Element={<span tabIndex={5}
                                                    data-arrow-index={10}
                                                    data-up-arrow-index={6}
                                                    data-down-arrow-index={7}
                                                    data-left-arrow-index={2}
                                                    data-right-arrow-index={10}
                                                    className={"color-login-link c-pointer text-style h6-semibold w-space"}> {ViewTextLabels.CONTACT_US} </span>} />
                                        }
                                    </span>
                                </div>
                                <div className={`w-p-100 h-2 mb-2 mx-auto d-flex justify-center h5-regular`}>
                                    <div className={`h-2 mx-auto bb-primary-background-pink-4 d-flex justify-center h5-regular w-p-45`}>
                                    </div>
                                    <div className=" min-w-6 h-2 d-flex justify-center align-center p-relative t-1 color-primary-accent w-space w-p-10">
                                        or
                                    </div>
                                    <div className={`h-2 mx-auto bb-primary-background-pink-4 d-flex justify-center h5-regular w-p-45`}>
                                    </div>
                                </div>
                                <div className=" d-flex w-p-100 flex-direction">
                                    {!IsTeamsApp &&
                                        <button
                                            data-arrow-index={9}
                                            data-up-arrow-index={8}
                                            data-down-arrow-index={9}
                                            data-left-arrow-index={2}
                                            data-right-arrow-index={9}
                                            tabIndex={4}
                                            onClick={() => ssoLogin("Microsoft")}
                                            disabled={state.IsSSOLoading || AuthService.isSSOLoginLoading()}
                                            className={`color-microsoft-inactive-email c-pointer justify-center align-center  d-flex h4-regular w-fill br-2 ${(state.IsSSOLoading || AuthService.isSSOLoginLoading()) ? 'disabled disabled-btn' : `secondary-btn ${IsDarkMode ? "bg-color-login-button" : "bg-color-login-button-background"} login-button-border-color`}`} style={{ textTransform: "none" }}>
                                            {(state.IsSSOLoading || AuthService.isSSOLoginLoading()) ? <span className="loader-secondary"></span> : <MicrosoftIcon className="mr-1 min-w-2" />}
                                            <span className={"w-space"}>{"Sign in with Microsoft"}</span>
                                        </button>
                                    }
                                    {!IsTeamsApp &&
                                        <button
                                            data-arrow-index={9}
                                            data-up-arrow-index={8}
                                            data-down-arrow-index={9}
                                            data-left-arrow-index={2}
                                            data-right-arrow-index={9}
                                            tabIndex={4}
                                            onClick={() => ssoLogin("Webex")}
                                            disabled={state.WebexLoading}
                                            className={`color-microsoft-inactive-email c-pointer justify-center align-center  d-flex h4-regular w-fill br-2 ${(state.WebexLoading || AuthService.isWebexLoginLoading()) ? 'disabled disabled-btn' : `secondary-btn ${IsDarkMode ? "bg-color-login-button" : "bg-color-login-button-background"} login-button-border-color`}`} style={{ textTransform: "none" }}>
                                            {(state.WebexLoading) ? <span className="loader-secondary"></span> : <WebexLoginIcon className="mr-1 min-w-2" />}
                                            <span className={"w-space"}>{"Sign in with Webex"}</span>
                                        </button>
                                    }
                                    {
                                        IsTeamsApp &&
                                        // (
                                        ((state.hostingEnvironment === "desktop" || state.hostingEnvironment === "android" || state.hostingEnvironment === "ios") && <MicrosoftSsoLogin />)
                                        // ||
                                        // (state.hostingEnvironment === "browser" && <MicroSoftTeamsWebSSO />)
                                        // )
                                    }

                                </div>
                            </form>
                        </div>
                        {!IsMobileDevice &&
                            !IsTeamsApp &&
                            <div className="h-p-15 d-flex justify-center min-h-12 min-w-50 w-p-50 mx-auto">
                                <div className="w-p-100 d-flex flex-direction align-center h-p-100">
                                    <div className="w-p-100 h-p-20 h6-medium mb-0 d-flex justify-between">
                                        <div className="">
                                            <a href='https://www.falkonsms.com/terms-of-service'
                                                target="_blank"
                                                data-arrow-index={3}
                                                data-up-arrow-index={1}
                                                data-down-arrow-index={1}
                                                data-left-arrow-index={5}
                                                data-right-arrow-index={4}
                                                className={"text-style color-login-text-field-font w-space f-link-hover"} rel="noopener noreferrer">
                                                {buttonLabels.TERMS_OF_SERVICE}
                                            </a>
                                        </div>
                                        <div className="">
                                            <a href='https://www.falkonsms.com/privacy-policy'
                                                target="_blank"
                                                data-arrow-index={4}
                                                data-up-arrow-index={2}
                                                data-down-arrow-index={2}
                                                data-left-arrow-index={3}
                                                data-right-arrow-index={5}
                                                className={"text-style color-login-text-field-font w-space f-link-hover"} rel="noopener noreferrer">
                                                {buttonLabels.PRIVACY_POLICY}
                                            </a>
                                        </div>
                                        <div className="">
                                            <a href='https://www.falkonsms.com/cookie-policy'
                                                target="_blank"
                                                data-arrow-index={5}
                                                data-up-arrow-index={2}
                                                data-down-arrow-index={3}
                                                data-left-arrow-index={4}
                                                data-right-arrow-index={9}
                                                className={"text-style color-login-text-field-font w-space f-link-hover"} rel="noopener noreferrer">
                                                {"Cookie Policy"}
                                            </a>
                                        </div>
                                        <div className="">
                                            <a href='https://www.falkonsms.com/acceptable-use-policy'
                                                target="_blank"
                                                data-arrow-index={5}
                                                data-up-arrow-index={2}
                                                data-down-arrow-index={4}
                                                data-left-arrow-index={4}
                                                data-right-arrow-index={9}
                                                className={"text-style color-login-text-field-font w-space f-link-hover"} rel="noopener noreferrer">
                                                {"Acceptable Use Policy"}
                                            </a>
                                        </div>
                                    </div>
                                    <div className="h-p-20 w-p-60">
                                        <div className="d-flex justify-between align-center mt-2">
                                            <div className="">
                                                <a href='https://www.falkonsms.com/'
                                                    data-arrow-index={1}
                                                    data-up-arrow-index={1}
                                                    data-down-arrow-index={0}
                                                    data-left-arrow-index={1}
                                                    data-right-arrow-index={2}
                                                    className={"text-style"} target="_blank" rel="noopener noreferrer">
                                                    <Tooltip title='Website' placement='bottom-end' classes={{ popper: "tooltip-z-index" }}>
                                                        <WebIcon />
                                                    </Tooltip>
                                                </a>
                                            </div>
                                            <div className="">
                                                <a href='https://www.facebook.com/falkonsystems2021'
                                                    data-arrow-index={2}
                                                    data-up-arrow-index={2}
                                                    data-down-arrow-index={0}
                                                    data-left-arrow-index={1}
                                                    data-right-arrow-index={3}
                                                    className={"text-style"} target="_blank" rel="noopener noreferrer">
                                                    <Tooltip title='Facebook' placement='bottom' classes={{ popper: "tooltip-z-index" }}>
                                                        <FbIcon />
                                                    </Tooltip>
                                                </a>
                                            </div>
                                            <div className="">
                                                <a href='https://www.linkedin.com/company/falkon-systems/'
                                                    data-arrow-index={3}
                                                    data-up-arrow-index={3}
                                                    data-down-arrow-index={0}
                                                    data-left-arrow-index={2}
                                                    data-right-arrow-index={4}
                                                    className={"text-style"} target="_blank" rel="noopener noreferrer">
                                                    <Tooltip title='LinkedIn' placement='bottom' classes={{ popper: "tooltip-z-index" }}>
                                                        <LinkdinIcon />
                                                    </Tooltip>
                                                </a>
                                            </div>
                                            <div className="">
                                                <a href='https://www.instagram.com/falkon_systems_/'
                                                    data-arrow-index={4}
                                                    data-up-arrow-index={2}
                                                    data-down-arrow-index={0}
                                                    data-left-arrow-index={3}
                                                    data-right-arrow-index={5}
                                                    className={"text-style"} target="_blank" rel="noopener noreferrer">
                                                    <Tooltip title='Instagram' placement='bottom' classes={{ popper: "tooltip-z-index" }}>
                                                        <InstaIcon />
                                                    </Tooltip>
                                                </a>
                                            </div>
                                            <div className="">
                                                <a href='https://www.youtube.com/channel/UCP_hOtnoImVPCleutK9mLtA'
                                                    data-arrow-index={5}
                                                    data-up-arrow-index={2}
                                                    data-down-arrow-index={0}
                                                    data-left-arrow-index={4}
                                                    data-right-arrow-index={6}
                                                    className={"text-style"} target="_blank" rel="noopener noreferrer">
                                                    <Tooltip title='Youtube' placement='bottom-start' classes={{ popper: "tooltip-z-index" }}>
                                                        <YoutubeIcon />
                                                    </Tooltip>
                                                </a>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        }
                    </div>
                </div>
            </div >

        </>
    )
}

export default (Login);
